import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DataGrid,
  ErrorMessage,
  ListHeader,
  LoadingSpinner,
  MenuButton,
  useFormatting,
} from "@msys/ui";
import { ReportProblemOutlined as ReportProblemOutlinedIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  LinearProgressProps,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ceil, floor, max } from "lodash-es";
import React from "react";
import { useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { AddDatanormOnlineImportationButton } from "../../features/pimImportations/buttons/AddDatanormOnlineImportationButton.js";
import { useDatanormOnlineAvailabilityQuery } from "../../features/pimImportations/buttons/AddDatanormOnlineImportationButton.generated.js";
import { AddPimImportationButton } from "../../features/pimImportations/buttons/AddPimImportationButton.js";
import { CancelPimImportationButton } from "../../features/pimImportations/buttons/CancelPimImportationButton.js";
import { DeletePimSupplierImportationsMenuItem } from "../../features/pimImportations/buttons/DeletePimSupplierImportationsMenuItem.js";
import {
  getPimImportationStatusKind,
  isPimImportationError,
  isPimImportationInProgress,
  PimImportationStatusKind,
} from "./pimImportationStatus.js";
import { useSupplierPimImportationsQuery } from "./SupplierPimImportations.generated.js";

interface Props {}

export const SupplierPimImportations = ({ ...props }: Props) => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const client = useApolloClient();
  const { getFormattedDate } = useFormatting();

  const { t } = useTranslate(["PimImportations", "ProductOverview", "Global"]);

  const statusKindLabels: Record<PimImportationStatusKind, string> = {
    waiting: t("Waiting", { ns: "PimImportations" }),
    indexing: t("Indexing", { ns: "PimImportations" }),
    indexed: t("Indexed", { ns: "PimImportations" }),
    deleted: t("Deleted", { ns: "PimImportations" }),
    error: t("failed", { ns: "PimImportations" }),
    hasDuplicate: t("has_duplicate", { ns: "PimImportations" }),
  };

  const query = useSupplierPimImportationsQuery({
    client,
    fetchPolicy: "no-cache",
    variables: {
      supplierId,
    },
  });

  const pimImportations = getDataOrNull(
    query.data?.pimImportations
  )?.importations;

  const supplier = getDataOrNull(query.data?.pimSupplier)?.supplier;

  const itekCompanyId = supplier?.itekCompanyIdentifier ?? undefined;

  const availabilityQuery = useDatanormOnlineAvailabilityQuery({
    client,
    variables: { itekCompanyId: itekCompanyId ?? "" },
    skip: !itekCompanyId,
    fetchPolicy: "network-only",
  });
  const datanormOnlineInfo = itekCompanyId
    ? getDataOrNull(availabilityQuery.data?.datanormOnlineAvailability)
    : null;
  // Settle once the availability check resolves — including on error — so a
  // failed check never leaves the empty-state stuck on a spinner; the
  // universally-available file-upload fallback is shown when availability is
  // unconfirmed.
  const isDatanormAvailabilitySettled =
    !itekCompanyId ||
    !availabilityQuery.loading ||
    Boolean(availabilityQuery.error);

  React.useEffect(() => {
    if (pimImportations?.some(importation => importation.isProcessing)) {
      query.startPolling(5000);
    } else {
      query.stopPolling();
    }

    return () => query.stopPolling();
  }, [pimImportations, query]);

  if (query.loading) return <LoadingSpinner />;
  if (query.error) return <ErrorMessage message={query.error.message} />;

  if (!supplier) return <ErrorMessage message="Supplier not found" />;

  // The user isn't entitled to this supplier's catalogues — show a no-access
  // message rather than falling through to the empty-state with upload/delete
  // CTAs (which getDataOrNull would otherwise make indistinguishable).
  if (query.data?.pimImportations.__typename === "MissingCapabilities") {
    return (
      <ErrorMessage
        message={t("Catalogue access not available", { ns: "PimImportations" })}
      />
    );
  }

  const isEmpty = !pimImportations || pimImportations.length === 0;

  // "Last catalogue update" reflects the most recent successfully indexed
  // catalogue; failed/deleted/in-progress imports are excluded, and it is
  // omitted entirely when none have been indexed yet.
  const lastCatalogueUpdate = max(
    pimImportations
      ?.filter(importation => importation.status === "indexed")
      .map(importation => importation.createdAt)
  );

  return (
    <>
      <ListHeader
        ActionButtons={
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              flexGrow: 0,
              flexShrink: 0,
              alignItems: "center",
            }}
          >
            {lastCatalogueUpdate && (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ mr: 0.5 }}
              >
                {t("Last catalogue update: {date}", {
                  ns: "PimImportations",
                  date: getFormattedDate(lastCatalogueUpdate),
                })}
              </Typography>
            )}
            {itekCompanyId && datanormOnlineInfo && (
              <AddDatanormOnlineImportationButton
                supplierId={supplierId}
                itekCompanyId={itekCompanyId}
                availability={datanormOnlineInfo}
                appearance="header"
                refetchQueries={[namedOperations.Query.SupplierPimImportations]}
              />
            )}
            <AddPimImportationButton
              supplierId={supplierId}
              appearance="header"
              refetchQueries={[namedOperations.Query.SupplierPimImportations]}
            />
            <MenuButton>
              <DeletePimSupplierImportationsMenuItem supplierId={supplierId} />
            </MenuButton>
          </Stack>
        }
        sx={{ marginBottom: 0.5 }}
      />
      {isEmpty ? (
        <Stack spacing={2} sx={{ alignItems: "center", paddingY: 8 }}>
          <Typography variant="body1" color="text.secondary">
            {t("Add catalogues via Datanorm sync or file upload.", {
              ns: "PimImportations",
            })}
          </Typography>
          {!isDatanormAvailabilitySettled ? (
            // Availability is still resolving — show a spinner in the CTA slot
            // rather than a button we'd have to swap out, so the empty-state
            // never looks actionless.
            <CircularProgress size={24} />
          ) : itekCompanyId && datanormOnlineInfo ? (
            <AddDatanormOnlineImportationButton
              supplierId={supplierId}
              itekCompanyId={itekCompanyId}
              availability={datanormOnlineInfo}
              appearance="cta"
              refetchQueries={[namedOperations.Query.SupplierPimImportations]}
            />
          ) : (
            <AddPimImportationButton
              supplierId={supplierId}
              appearance="cta"
              refetchQueries={[namedOperations.Query.SupplierPimImportations]}
            />
          )}
        </Stack>
      ) : (
        <DataGrid
          columns={[
            {
              field: "filename",
              headerName: t("Filename", { ns: "Global" }),
              flex: 1,
            },
            {
              field: "sourceDescription",
              headerName: t("Description", { ns: "Global" }),
              flex: 1,
              valueGetter: (value, row) => row.sourceDescription ?? "",
            },
            {
              field: "createdAt",
              headerName: t("Created at", { ns: "ProductOverview" }),
              flex: 1,
              valueFormatter(value) {
                return getFormattedDate(value);
              },
            },
            {
              field: "status",
              headerName: t("Status", { ns: "Global" }),
              flex: 1,
              renderCell: ({ row }) => {
                const kind = getPimImportationStatusKind(
                  row.status,
                  row.completionPercentage
                );
                const isError = isPimImportationError(kind);
                const inProgress = isPimImportationInProgress(kind);

                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      color: "error",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <Chip
                      color={
                        isError
                          ? "error"
                          : inProgress
                            ? "info"
                            : kind === "indexed"
                              ? "success"
                              : "warning"
                      }
                      icon={isError ? <ReportProblemOutlinedIcon /> : undefined}
                      variant="filled"
                      size="small"
                      label={statusKindLabels[kind]}
                      sx={{
                        ".MuiChip-label": { textTransform: "capitalize" },
                      }}
                    />
                    {inProgress ? (
                      <LinearProgressWithLabel
                        value={row.completionPercentage * 100}
                        sx={{ flex: 1 }}
                      />
                    ) : null}
                  </Stack>
                );
              },
            },
            {
              field: "totalProcessedProducts",
              headerName: t("Product count", { ns: "PimImportations" }),
              flex: 0.5,
              renderCell: ({ row }) => {
                return (
                  <Typography variant="body2">
                    {`${row?.totalProcessedProducts ?? 0} ${t("products")}`}
                  </Typography>
                );
              },
            },
            {
              field: "actions",
              headerName: "",
              flex: 0,
              renderCell: ({ row }) => {
                const kind = getPimImportationStatusKind(
                  row.status,
                  row.completionPercentage
                );
                return isPimImportationInProgress(kind) ? (
                  <CancelPimImportationButton pimImportation={row} />
                ) : undefined;
              },
            },
          ]}
          rows={pimImportations}
          paginationMode="client"
        />
      )}
    </>
  );
};

function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number }
) {
  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          mr: 1,
        }}
      >
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box
        sx={{
          width: 35,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {`${
            floor(props.value) === 0 ? ceil(props.value) : floor(props.value)
          }%`}
        </Typography>
      </Box>
    </Box>
  );
}
